/**
 * Maintenance mode (#3561, BE ourteam-backend#4630).
 *
 * One switch. Turning it on puts the maintenance banner in front of every
 * signed-in customer; nobody is emailed. The banner is the whole notice.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
    AlertTriangleIcon,
    CheckCircle2Icon,
    Loader2Icon,
    WrenchIcon,
} from "lucide-react";
import AuthLayout from "@container/layout/auth";
import { Card, CardContent } from "@component/card";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { Textarea } from "@component/textarea";
import { Switch } from "@component/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@component/alert-dialog";
import {
    formatLocal,
    utcToZonedWallClock,
    zonedWallClockToUtc,
} from "./components/window-format";
import { getMaintenanceAdmin, updateMaintenance } from "@service/admin";
import { MaintenanceState } from "@type/admin";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";

const BROWSER_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** The zones our workspaces are actually in, plus whatever this browser is set
 *  to, so an operator elsewhere is never forced to do the arithmetic. */
const ZONE_OPTIONS = Array.from(
    new Set([
        BROWSER_ZONE,
        "Asia/Kuala_Lumpur",
        "Asia/Singapore",
        "Asia/Jakarta",
        "Asia/Manila",
        "UTC",
    ])
);

function errorText(err: unknown, fallback: string): string {
    if (axios.isAxiosError(err)) {
        return (
            err.response?.data?.errors?.[0] ||
            err.response?.data?.message ||
            fallback
        );
    }
    return fallback;
}

export default function AdminMaintenancePage() {
    // AuthLayout owns the SnackbarProvider, so useSnackbar must be called from a
    // child rendered *inside* it — not from this top-level page.
    return (
        <AuthLayout>
            <MaintenanceContent />
        </AuthLayout>
    );
}

function MaintenanceContent() {
    const snackbar = useSnackbar();

    // ── switch ────────────────────────────────────────────────────────────────
    const [state, setState] = useState<MaintenanceState | null>(null);
    const [isLoadingState, setIsLoadingState] = useState(true);
    const [isSavingState, setIsSavingState] = useState(false);
    const [pendingActive, setPendingActive] = useState<boolean | null>(null);
    const [message, setMessage] = useState("");
    const [windowZone, setWindowZone] = useState(
        ZONE_OPTIONS.includes("Asia/Kuala_Lumpur") ? "Asia/Kuala_Lumpur" : BROWSER_ZONE
    );
    const [startsLocal, setStartsLocal] = useState("");
    const [endsLocal, setEndsLocal] = useState("");

    const loadState = () => {
        setIsLoadingState(true);
        getMaintenanceAdmin()
            .then((resp) => {
                const data = resp.data?.data ?? null;
                setState(data);
                setMessage(data?.message ?? "");
                if (data?.starts_at) setStartsLocal(utcToZonedWallClock(data.starts_at, windowZone));
                if (data?.ends_at) setEndsLocal(utcToZonedWallClock(data.ends_at, windowZone));
            })
            .catch((err) => {
                snackbar.showError(
                    "Could not read maintenance state",
                    errorText(err, "Please refresh the page and try again.")
                );
                catchErrorLog(`getMaintenanceAdmin error: ${err}`);
            })
            .finally(() => setIsLoadingState(false));
    };

    // Once, on open. `windowZone` is deliberately not a dependency: the typed
    // wall clock belongs to whichever zone is selected, so changing the zone
    // means "18:00 somewhere else", not "re-render this instant". Re-running the
    // load on a zone change would overwrite the operator's edits to say so.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(loadState, []);

    const saveState = async (isActive: boolean) => {
        setIsSavingState(true);
        try {
            const resp = await updateMaintenance({
                is_active: isActive,
                starts_at: startsLocal ? zonedWallClockToUtc(startsLocal, windowZone) : null,
                ends_at: endsLocal ? zonedWallClockToUtc(endsLocal, windowZone) : null,
                message: message.trim() || null,
            });
            if (resp.data?.code && resp.data.code !== 200) {
                snackbar.showError(
                    "Could not save",
                    resp.data?.errors?.[0] || resp.data?.message || "Please try again."
                );
                return;
            }
            snackbar.showSuccess(
                isActive ? "Maintenance mode is on" : "Maintenance mode is off",
                isActive
                    ? "Everyone signed in now sees the notice."
                    : "The notice is no longer shown."
            );
            loadState();
        } catch (err) {
            snackbar.showError("Could not save", errorText(err, "Please try again."));
            catchErrorLog(`updateMaintenance error: ${err}`);
        } finally {
            setIsSavingState(false);
        }
    };

    const isActive = !!state?.is_active;

    return (
        <div className="space-y-6 pb-10">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
                    <WrenchIcon className="h-5 w-5" />
                </div>
                <div>
                    <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Maintenance</h1>
                    <p className="text-sm text-neutral-500">
                        Show every signed-in customer a banner saying the platform is under maintenance.
                    </p>
                </div>
            </div>

            {/* ── The switch ─────────────────────────────────────────────────── */}
            <Card className="max-w-3xl">
                <CardContent className="space-y-6 p-6 sm:p-8">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <h2 className="my-0 text-base font-semibold text-gray-900">Maintenance status</h2>
                            {isLoadingState ? (
                                <p className="my-0 mt-1 text-sm text-neutral-500">Reading current state…</p>
                            ) : isActive ? (
                                <p className="my-0 mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-amber-700">
                                    <AlertTriangleIcon className="h-4 w-4" />
                                    On — everyone signed in sees the notice
                                </p>
                            ) : (
                                <p className="my-0 mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700">
                                    <CheckCircle2Icon className="h-4 w-4" />
                                    Off — nothing is shown to customers
                                </p>
                            )}
                            {!isLoadingState && (state?.updated_at || state?.updated_by_name) && (
                                <p className="my-0 mt-1 text-xs text-neutral-500">
                                    Last changed {formatLocal(state?.updated_at ?? null)}
                                    {state?.updated_by_name ? ` by ${state.updated_by_name}` : ""}
                                </p>
                            )}
                        </div>
                        {/* The switch opens a confirmation rather than acting. This is
                            customer-visible either way round: turning it on shows an
                            outage notice to everyone, turning it off takes down a
                            warning that may still be true. */}
                        <Switch
                            checked={isActive}
                            disabled={isLoadingState || isSavingState}
                            onCheckedChange={(next) => setPendingActive(next)}
                            aria-label="Maintenance mode"
                        />
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <div className="space-y-2">
                            <Label className="block text-sm font-medium text-gray-900">Timezone</Label>
                            <Select value={windowZone} onValueChange={setWindowZone}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {ZONE_OPTIONS.map((z) => (
                                        <SelectItem key={z} value={z}>{z}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label className="block text-sm font-medium text-gray-900">Starts (optional)</Label>
                            <Input
                                type="datetime-local"
                                value={startsLocal}
                                onChange={(e) => setStartsLocal(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="block text-sm font-medium text-gray-900">Ends (optional)</Label>
                            <Input
                                type="datetime-local"
                                value={endsLocal}
                                onChange={(e) => setEndsLocal(e.target.value)}
                            />
                        </div>
                    </div>
                    <p className="my-0 text-xs text-neutral-500">
                        Times are read in the timezone above, not this browser&rsquo;s. Leaving
                        &ldquo;Ends&rdquo; empty is fine — the notice then says work is in progress
                        without naming a finish.
                    </p>

                    <div className="space-y-2">
                        <Label className="block text-sm font-medium text-gray-900">Message (optional)</Label>
                        <Textarea
                            rows={2}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Scheduled database upgrade. Sign-in and applications are unaffected."
                        />
                        <p className="my-0 text-xs text-neutral-500">
                            Shown in the banner. Plain text — say what is affected, not just that
                            something is.
                        </p>
                    </div>

                    <div className="flex justify-end">
                        <Button
                            variant="outline"
                            disabled={isLoadingState || isSavingState}
                            onClick={() => void saveState(isActive)}
                        >
                            {isSavingState && <Loader2Icon className="h-4 w-4 animate-spin" />}
                            Save window and message
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Confirm: toggle */}
            <AlertDialog
                open={pendingActive !== null}
                onOpenChange={(open) => !open && setPendingActive(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {pendingActive ? "Turn maintenance mode on?" : "Turn maintenance mode off?"}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {pendingActive
                                ? "Everyone signed in sees a maintenance notice immediately. Nobody is locked out — this states that we are working on the platform, it does not block anything."
                                : "The notice disappears for everyone immediately. Turn it off only once the work is genuinely finished."}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                const next = pendingActive;
                                setPendingActive(null);
                                if (next !== null) void saveState(next);
                            }}
                        >
                            {pendingActive ? "Turn on" : "Turn off"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
